import { ClienteScreening, StateOption } from '../../../../../models/SimulationBank/SimulationBankModels';
import { normalizeText, UF_NAMES } from '../StepDigitacao/digitacaoFormUtils';

// Normalização do retorno de simulation/screening/cpf/{cpf} para o formato dos campos do
// Step1. Formato real (base Dataprev/BevIAjuda), chaves em maiúsculas:
//   { status: true, data: { CONSULTA: {
//       CADASTRAIS: { CPF, NOME, NASC ("dd/MM/yyyy"), TELEFONE, ... },
//       ENDERECOS: [{ CIDADE, UF, ... }, ...],
//   } } }
// A leitura ainda aceita apelidos alternativos (minúsculo/snake_case) como fallback: um
// retorno bom em chave inesperada vira "sem dados" e o parceiro digita tudo de novo —
// perde-se a conveniência sem nenhum sinal de erro.

const pick = (source: Record<string, any>, keys: string[]): string | undefined => {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number') return String(value);
    }
    return undefined;
};

// O body pode vir cru ou embrulhado em `data`/`cliente`/`data.CONSULTA` — mesmo
// tratamento que LocationsService recebe em Step1Form (`response?.data ?? response`).
const unwrap = (payload: any): Record<string, any> | null => {
    if (!payload || typeof payload !== 'object') return null;
    const inner = payload.data ?? payload.cliente ?? payload.result ?? payload;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return null;
    return inner.CONSULTA ?? inner.consulta ?? inner;
};

// Primeiro endereço da lista — a base pode devolver vários (residencial, comercial,
// anteriores); POSICAO "1" é o mais recente/relevante, e na ausência do campo o
// primeiro item da lista já é o critério usado no restante do app.
const firstEndereco = (source: Record<string, any>): Record<string, any> => {
    const enderecos = source.ENDERECOS ?? source.enderecos ?? source.enderecos_list;
    if (!Array.isArray(enderecos) || !enderecos.length) return {};
    return enderecos.find((e) => String(e?.POSICAO ?? e?.posicao) === '1') ?? enderecos[0] ?? {};
};

// `input[type="date"]` só aceita yyyy-MM-dd. A base pode devolver ISO com horário,
// dd/MM/yyyy ou já no formato do input; qualquer outra coisa vira undefined e o campo
// segue vazio para o parceiro preencher.
export const normalizeBirthDate = (value?: string): string | undefined => {
    if (!value) return undefined;
    const raw = value.trim();

    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const br = raw.match(/^(\d{2})[/\-.](\d{2})[/\-.](\d{4})/);
    if (br) return `${br[3]}-${br[2]}-${br[1]}`;

    return undefined;
};

export const extractClienteScreening = (payload: any): ClienteScreening | null => {
    const source = unwrap(payload);
    if (!source) return null;

    // CADASTRAIS concentra CPF/nome/nascimento; ENDERECOS[0] traz UF/cidade. Quando o
    // retorno vier achatado (sem esses blocos) os `pick` caem direto no nível raiz.
    const cadastrais = source.CADASTRAIS ?? source.cadastrais ?? source;
    const endereco = firstEndereco(source);

    return {
        cpf: pick(cadastrais, ['CPF', 'cpf', 'documento', 'nr_cpf']),
        nome_completo: pick(cadastrais, ['NOME', 'nome_completo', 'nome', 'nomeCompleto', 'name']),
        data_nascimento: normalizeBirthDate(
            pick(cadastrais, ['NASC', 'data_nascimento', 'dataNascimento', 'nascimento', 'birth_date', 'data_de_nascimento'])
        ),
        telefone_celular: pick(cadastrais, ["TELEFONE", "telefone"]),
        estado: pick(endereco, ['UF', 'uf', 'estado', 'sigla_uf', 'state']) ?? pick(source, ['UF', 'estado', 'uf', 'sigla_uf', 'state']),
        cidade: pick(endereco, ['CIDADE', 'cidade', 'municipio', 'city', 'nome_cidade']) ?? pick(source, ['CIDADE', 'cidade', 'municipio', 'city', 'nome_cidade']),
    };
};

// CA6 — conferência de chave. Só há divergência quando a resposta traz um CPF e ele é
// outro; resposta sem CPF não confirma nem contradiz a chave e segue válida (a consulta
// já foi feita pelo CPF na URL).
export const matchesCpf = (returnedCpf: string | undefined, typedCpf: string): boolean => {
    if (!returnedCpf) return true;
    return returnedCpf.replace(/\D/g, '') === typedCpf.replace(/\D/g, '');
};

// O select de Estado guarda a sigla (ver handleStateChange), mas a base pode devolver
// sigla, nome por extenso ou código IBGE — casamos contra as três formas da opção.
export const matchStateOption = (states: StateOption[], rawEstado?: string): StateOption | null => {
    if (!rawEstado) return null;

    const raw = normalizeText(rawEstado);
    const candidates = new Set([raw]);

    const ufName = UF_NAMES[rawEstado.trim().toUpperCase()];
    if (ufName) candidates.add(normalizeText(ufName));

    const ufSigla = Object.keys(UF_NAMES).find((sigla) => normalizeText(UF_NAMES[sigla]) === raw);
    if (ufSigla) candidates.add(normalizeText(ufSigla));

    return (
        states.find(
            (s) =>
                candidates.has(normalizeText(s.value)) ||
                candidates.has(normalizeText(s.label)) ||
                candidates.has(normalizeText(s.uf ?? ''))
        ) ?? null
    );
};

export const matchCityOption = <T extends { value: string; label: string }>(
    cities: T[],
    rawCidade?: string
): T | null => {
    if (!rawCidade) return null;
    const raw = normalizeText(rawCidade);
    return cities.find((c) => normalizeText(c.label) === raw || normalizeText(c.value) === raw) ?? null;
};
